import { useState, useEffect, useCallback } from 'react';
import { format, parse } from 'date-fns';
import { TranslationKeyEnum } from '../types/constants';

export type Globalization = {
  locale: string;
};

/**
 * Hook that provides globalization utilities like translations and formatting
 */
const useGlobalization = (
  { locale = 'en-US' }: Globalization = { locale: 'en-US' }
): {
  configReady: boolean;
  t: (key: TranslationKeyEnum, ...args: string[]) => string;
  formatCurrency: (value: number) => string;
  formatNumber: (value: number) => string;
  formatDate: (dateString: string) => string;
  formatDateMonthYear: (dateString: string) => string;
  formatMonthDateYear: (dateString: string) => string;
} => {
  // Use pre-loaded translations from window (loaded in index.html)
  const [cmsTranslations, setCmsTranslations] = useState<Record<string, string>>(
    () => window.__FSL_TRANSLATIONS__ || {}
  );
  const [isLoading, setIsLoading] = useState(() => !window.__FSL_TRANSLATIONS_READY__);

  // Listen for translation ready event
  useEffect(() => {
    const updateTranslations = () => {
      const translations = window.__FSL_TRANSLATIONS__ || {};
      console.log(`[useGlobalization] Loaded ${Object.keys(translations).length} translations for ${locale}`);
      setCmsTranslations(translations);
      setIsLoading(false);
    };

    // Update immediately if already ready
    if (window.__FSL_TRANSLATIONS_READY__) {
      updateTranslations();
    }

    window.addEventListener('fslTranslationsReady', updateTranslations);
    return () => window.removeEventListener('fslTranslationsReady', updateTranslations);
  }, [locale]);

  const getTranslation = useCallback((key: TranslationKeyEnum, ...args: string[]) => {
    if (isLoading) return '';
    
    const template = cmsTranslations[key];
    if (!template) {
      console.info(`Missing translation: "${key}"`);
      return key;
    }

    return args.length
      ? template.replace(/\{(\d+)\}/g, (_m: string, i: string) => args[Number(i)] || _m)
      : template;
  }, [cmsTranslations, isLoading]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(value);

  const formatNumber = (value: number) =>
    new Intl.NumberFormat(locale).format(value);

  const parseAndFormat = (dateString: string, formatStr: string) =>
    format(parse(dateString, 'yyyy-MM-dd', new Date()), formatStr);

  const formatDate = (dateString: string) => parseAndFormat(dateString, 'PP');
  const formatDateMonthYear = (dateString: string) => parseAndFormat(dateString, 'MMMM yyyy');
  const formatMonthDateYear = (dateString: string) => parseAndFormat(dateString, 'MM/dd/yyyy');

  return {
    configReady: !isLoading && Object.keys(cmsTranslations).length > 0,
    t: getTranslation,
    formatCurrency,
    formatNumber,
    formatDate,
    formatDateMonthYear,
    formatMonthDateYear,
  };
};

export default useGlobalization;
